/**
 * Formatação de moeda no padrão brasileiro (R$ 1.234,56) e conversão de
 * valores para extenso (ex: "um mil, duzentos e trinta e quatro reais e
 * cinquenta e seis centavos").
 *
 * Port de extenso.py da prova de conceito. O arredondamento reproduz o do
 * Python (valor decimal exato do double, empate para o par), para que os
 * valores mostrados sejam idênticos aos da versão anterior.
 */

const UNIDADES = ["", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove"];
const DEZ_A_DEZENOVE = ["dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
const DEZENAS = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];
const CENTENAS = ["", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"];
const ESCALA_SINGULAR = ["", "mil", "milhão", "bilhão", "trilhão"];
const ESCALA_PLURAL = ["", "mil", "milhões", "bilhões", "trilhões"];
const ESCALAS_QUE_PEDEM_DE = ["milhão", "milhões", "bilhão", "bilhões", "trilhão", "trilhões"];

/**
 * Arredonda como o Python (`round(x, casas)` / `f"{x:.2f}"`): parte da
 * expansão decimal exata do double e desempata para o dígito par.
 * Retorna o número já formatado com `casas` decimais, sem separador de milhar.
 */
export function arredondarPython(valor: number, casas: number): string {
  if (!Number.isFinite(valor)) return String(valor);
  const negativo = valor < 0 || Object.is(valor, -0);
  const exato = Math.abs(valor).toFixed(100);
  const [inteira, fracao] = exato.split(".");
  const manter = inteira + fracao.slice(0, casas);
  const resto = fracao.slice(casas);
  let digitos = manter.split("").map(Number);

  const primeiro = Number(resto[0] ?? "0");
  const demaisZerados = /^0*$/.test(resto.slice(1));
  const ultimoImpar = (digitos[digitos.length - 1] ?? 0) % 2 === 1;
  const sobe = primeiro > 5 || (primeiro === 5 && (!demaisZerados || ultimoImpar));

  if (sobe) {
    let i = digitos.length - 1;
    while (i >= 0) {
      if (digitos[i] === 9) {
        digitos[i] = 0;
        i--;
      } else {
        digitos[i]++;
        break;
      }
    }
    if (i < 0) digitos = [1, ...digitos];
  }

  const texto = digitos.join("");
  const corte = texto.length - casas;
  let parteInteira = texto.slice(0, corte).replace(/^0+(?=\d)/, "");
  if (parteInteira === "") parteInteira = "0";
  const resultado = casas > 0 ? `${parteInteira}.${texto.slice(corte)}` : parteInteira;
  const zero = /^[0.]+$/.test(resultado);
  return negativo && !zero ? `-${resultado}` : resultado;
}

/** Arredondamento do Python para inteiro (`round(x)`): empate para o par. */
function arredondarInteiroPython(valor: number): number {
  return Number(arredondarPython(valor, 0));
}

/** 871065.65 -> '871.065,65' (padrão BR: ponto pra milhar, vírgula pros centavos). */
export function formatarMoeda(valor: number | string | null | undefined): string {
  const numero = Number(valor || 0);
  const texto = arredondarPython(numero, 2);
  const negativo = texto.startsWith("-");
  const [inteira, fracao] = texto.replace("-", "").split(".");
  const comMilhar = inteira.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${negativo ? "-" : ""}${comMilhar},${fracao}`;
}

/** Converte um número de 0 a 999 para texto em português. */
function blocoExtenso(n: number): string {
  if (n === 0) return "";
  if (n === 100) return "cem";
  const centena = Math.floor(n / 100);
  const resto = n % 100;
  const partes: string[] = [];
  if (centena) partes.push(CENTENAS[centena]);
  if (resto) {
    if (resto < 10) {
      partes.push(UNIDADES[resto]);
    } else if (resto < 20) {
      partes.push(DEZ_A_DEZENOVE[resto - 10]);
    } else {
      const dezena = Math.floor(resto / 10);
      const unidade = resto % 10;
      let texto = DEZENAS[dezena];
      if (unidade) texto += " e " + UNIDADES[unidade];
      partes.push(texto);
    }
  }
  return partes.join(" e ");
}

/** Converte um inteiro não-negativo para texto em português. */
export function numeroPorExtenso(valor: number): string {
  const n = Math.trunc(valor);
  if (n === 0) return "zero";
  const blocos: number[] = [];
  let temp = n;
  while (temp > 0) {
    blocos.push(temp % 1000);
    temp = Math.floor(temp / 1000);
  }

  const partes: string[] = [];
  for (let i = blocos.length - 1; i >= 0; i--) {
    const bloco = blocos[i];
    if (bloco === 0) continue;
    const texto = blocoExtenso(bloco);
    if (i === 0) {
      partes.push(texto);
    } else if (i === 1) {
      partes.push(bloco === 1 ? "mil" : texto + " mil");
    } else {
      const escala = bloco === 1 ? ESCALA_SINGULAR[i] : ESCALA_PLURAL[i];
      partes.push(texto + " " + escala);
    }
  }

  if (partes.length === 1) return partes[0];
  const ultimoBloco = blocos[0];
  if (ultimoBloco > 0 && ultimoBloco < 100) {
    return partes.slice(0, -1).join(", ") + " e " + partes[partes.length - 1];
  }
  return partes.join(", ");
}

/** 1234.56 -> 'um mil, duzentos e trinta e quatro reais e cinquenta e seis centavos'. */
export function valorPorExtenso(valor: number | string | null | undefined): string {
  const arredondado = Number(arredondarPython(Number(valor || 0), 2));
  const reais = Math.trunc(arredondado);
  const centavos = arredondarInteiroPython((arredondado - reais) * 100);

  if (reais === 0 && centavos === 0) return "zero reais";

  const partes: string[] = [];
  if (reais > 0) {
    const textoReais = numeroPorExtenso(reais);
    const sufixo = reais === 1 ? "real" : "reais";
    const palavras = textoReais.split(" ");
    const conector = ESCALAS_QUE_PEDEM_DE.includes(palavras[palavras.length - 1]) ? " de " : " ";
    partes.push(`${textoReais}${conector}${sufixo}`);
  }
  if (centavos > 0) {
    const textoCentavos = numeroPorExtenso(centavos);
    const sufixo = centavos === 1 ? "centavo" : "centavos";
    partes.push(`${textoCentavos} ${sufixo}`);
  }
  return partes.join(" e ");
}
